import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Header";
import emergency from "../assets/img/HomePage/emergency.svg";
import referral from "../assets/img/HomePage/referral.svg";
import guidance from "../assets/img/HomePage/guidance.svg";
import laws from "../assets/img/HomePage/laws.svg";
import ModalAbout from "../components/ModalAbout";
import { makeStyles } from "@mui/styles";
import Options from "../components/Options";
import { useDispatch, useSelector } from "react-redux";
import { changeTab } from "../redux/TabSlice";
import { getTitleFromAppContent, getContentTitle } from "../helpers/utils";
import { setBackToGuide } from "../redux/PageSlice";
import PageBackButton from "./PageBackButton";
import SearchIcon from "@mui/icons-material/Search";
import { trackAppOpen, trackPageView } from "../helpers/api";

const useStyles = makeStyles((theme) => ({
  textColor: {
    color: theme.palette.primary.main,
  },
  sectionBgLight: {
    backgroundColor: "#F6F1F4",
  },
  emergencyBg: {
    backgroundColor: theme.palette.emergency.light,
  },
  emergencyTextBg: {
    backgroundColor: theme.palette.emergency.main,
  },
  referralBg: {
    backgroundColor: "#A94E76",
  },
  referralTextBg: {
    backgroundColor: "#8E264E",
  },
  guidanceBg: {
    backgroundColor: theme.palette.guidance.light,
  },
  guidanceTextBg: {
    backgroundColor: theme.palette.guidance.main,
  },
  lawsBg: {
    backgroundColor: theme.palette.laws.light,
  },
  lawsTextBg: {
    backgroundColor: theme.palette.laws.main,
  },
}));

const tileClass =
  "flex flex-col justify-between cursor-pointer rounded-xl min-h-[11rem] w-full touch-manipulation";

const HomePopUp = ({ onClose }) => {
  const classes = useStyles();
  const { selLanguageId } = useSelector((state) => state.language);
  const { selDistrictId } = useSelector((state) => state.district);
  const { selProvinceId } = useSelector((state) => state.province);
  const { appContent } = useSelector((state) => state.appContent);
  const [openAbout, setOpenAbout] = useState(false);
  const [tabSelect, setTabSelect] = useState("HumQadam");
  const [emergencyTitle, setEmergencyTitle] = useState("");
  const [referralTitle, setReferralTitle] = useState("");
  const [guidanceTitle, setGuidanceTitle] = useState("");
  const [lawsTitle, setLawsTitle] = useState("");
  const dispatch = useDispatch();
  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => {
    setOpenAbout(false);
  };

  useEffect(() => {
    if (appContent && appContent.length > 0) {
      setEmergencyTitle(getTitleFromAppContent(appContent, "Emergency Services"));
      setReferralTitle(
        getContentTitle(appContent, [
          "Support Providing Organizations",
          "Referral",
          "Referral Guide",
        ])
      );
      setGuidanceTitle(getTitleFromAppContent(appContent, "Step by Step Guidance"));
      setLawsTitle(getTitleFromAppContent(appContent, "Relevant Laws"));
    }
  }, [appContent]);

  const hasTrackedOpen = useRef(false);
  useEffect(() => {
    dispatch(setBackToGuide(false));

    if (!hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackAppOpen({
        province: selProvinceId,
        district: selDistrictId,
        language: selLanguageId,
      });
    }
  }, [dispatch, selProvinceId, selDistrictId, selLanguageId]);

  useEffect(() => {
    dispatch(changeTab(tabSelect));

    if (tabSelect && tabSelect !== "HumQadam") {
      trackPageView(tabSelect, {
        province: selProvinceId,
        district: selDistrictId,
        language: selLanguageId,
      });
    }
  }, [tabSelect, dispatch, selProvinceId, selDistrictId, selLanguageId]);

  return (
    <div className={`${classes.sectionBgLight} min-h-[100dvh] w-full flex flex-col`}>
      <Header />

      <div className="flex items-start justify-between gap-2 w-full pr-2">
        <PageBackButton onClick={() => onClose()} />
        <Options handleOpen={handleOpen} />
      </div>

      <div className="px-5 pb-16 hq-safe-bottom w-full flex-1">
        <button
          type="button"
          className={`flex items-center w-full max-w-2xl mx-auto bg-white rounded-full py-3 px-5 cursor-pointer border border-[#A94E76] hover:shadow-sm touch-manipulation`}
          onClick={() => setTabSelect("GlobalSearch")}
        >
          <SearchIcon color="primary" />
          <span className="text-gray-400 ml-2 flex-1 text-left">
            {getTitleFromAppContent(appContent, "Search...") || "Search..."}
          </span>
        </button>

        <div className="grid grid-cols-2 gap-3 w-full max-w-2xl mx-auto mt-4">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setTabSelect("EmergencyServices")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setTabSelect("EmergencyServices");
            }}
            className={`${tileClass} ${classes.emergencyBg}`}
          >
            <div className="flex-1 flex items-center justify-center py-4">
              <img src={emergency} alt="" className="h-24 w-auto max-w-[80%] object-contain" />
            </div>
            <p
              className={`${classes.emergencyTextBg} text-center rounded-b-xl p-2 text-sm text-white`}
            >
              {emergencyTitle}
            </p>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => setTabSelect("ReferralDirectoryEntitiesGrid")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                setTabSelect("ReferralDirectoryEntitiesGrid");
            }}
            className={`${tileClass} ${classes.referralBg}`}
          >
            <div className="flex-1 flex items-center justify-center py-4">
              <img src={referral} alt="" className="h-24 w-auto max-w-[80%] object-contain" />
            </div>
            <p
              className={`${classes.referralTextBg} text-center rounded-b-xl p-2 text-sm text-white`}
            >
              {referralTitle}
            </p>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => setTabSelect("StepGuide")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setTabSelect("StepGuide");
            }}
            className={`${tileClass} ${classes.guidanceBg}`}
          >
            <div className="flex-1 flex items-center justify-center py-4">
              <img src={guidance} alt="" className="h-24 w-auto max-w-[80%] object-contain" />
            </div>
            <p
              className={`${classes.guidanceTextBg} text-center rounded-b-xl p-2 text-sm text-white`}
            >
              {guidanceTitle}
            </p>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => setTabSelect("Laws")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setTabSelect("Laws");
            }}
            className={`${tileClass} ${classes.lawsBg}`}
          >
            <div className="flex-1 flex items-center justify-center py-4">
              <img src={laws} alt="" className="h-24 w-auto max-w-[80%] object-contain" />
            </div>
            <p
              className={`${classes.lawsTextBg} text-center rounded-b-xl p-2 text-sm text-white`}
            >
              {lawsTitle}
            </p>
          </div>
        </div>
      </div>

      <ModalAbout open={openAbout} handleClose={handleClose} />
    </div>
  );
};

export default HomePopUp;
